import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import AppShell from "@/components/layout/AppShell";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CalendarDays, DollarSign, Play } from "lucide-react";
import { toast } from "sonner";

export default function Payroll() {
    const { activeHotelId } = useAuth();
    const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
    const [slips, setSlips] = useState([]);
    const [loading, setLoading] = useState(false);

    const load = async () => {
        if (!activeHotelId) return;
        const { data } = await api.get(`/payroll/payslips?hotel_id=${activeHotelId}&month=${month}`);
        setSlips(data);
    };
    useEffect(() => { load(); }, [activeHotelId, month]);

    const generate = async () => {
        setLoading(true);
        try {
            const { data } = await api.post("/payroll/generate", { hotel_id: activeHotelId, month });
            toast.success(`Generated ${data.payslips_generated} payslips`);
            load();
        } catch (e) { toast.error(formatApiError(e)); }
        setLoading(false);
    };

    const markPaid = async (id) => {
        try { await api.post(`/payroll/payslips/${id}/mark-paid`); toast.success("Marked paid"); load(); }
        catch (e) { toast.error(formatApiError(e)); }
    };

    const total = slips.reduce((s, p) => s + (p.net_pay || 0), 0);
    const paidTotal = slips.filter(p => p.status === "paid").reduce((s, p) => s + (p.net_pay || 0), 0);

    return (
        <AppShell title="Payroll" subtitle={`${slips.length} payslips for ${month}`}>
            <div className="flex flex-wrap items-end gap-3 mb-6">
                <div>
                    <Label className="text-xs uppercase tracking-wider">Month</Label>
                    <Input type="month" value={month} onChange={e => setMonth(e.target.value)} className="w-40" data-testid="payroll-month" />
                </div>
                <Button onClick={generate} disabled={loading} className="bg-[#0A1C3E] rounded-sm" data-testid="payroll-generate">
                    <Play className="w-4 h-4 mr-1" /> Generate Payroll
                </Button>
            </div>

            <div className="grid grid-cols-3 gap-4 mb-6">
                <Card className="p-5 border border-slate-200 rounded-md shadow-none">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Total Payroll</div>
                    <div className="text-3xl font-display font-bold mt-2">₹{total.toLocaleString()}</div>
                </Card>
                <Card className="p-5 border border-slate-200 rounded-md shadow-none">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Paid</div>
                    <div className="text-3xl font-display font-bold mt-2 text-emerald-700">₹{paidTotal.toLocaleString()}</div>
                </Card>
                <Card className="p-5 border border-slate-200 rounded-md shadow-none">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Pending</div>
                    <div className="text-3xl font-display font-bold mt-2 text-orange-600">₹{(total - paidTotal).toLocaleString()}</div>
                </Card>
            </div>

            <Card className="border border-slate-200 rounded-md overflow-hidden shadow-none">
                <table className="w-full">
                    <thead className="bg-slate-50 border-b border-slate-200 text-xs font-semibold text-slate-500 uppercase tracking-wider text-left">
                        <tr><th className="px-4 py-3">Employee</th><th className="px-4 py-3">Base</th><th className="px-4 py-3">Present</th><th className="px-4 py-3">Absent</th><th className="px-4 py-3">Deductions</th><th className="px-4 py-3">Statutory</th><th className="px-4 py-3">Net Pay</th><th className="px-4 py-3">Status</th><th className="px-4 py-3 text-right">Actions</th></tr>
                    </thead>
                    <tbody>
                        {slips.map(p => (
                            <tr key={p.id} className="border-b border-slate-100" data-testid={`slip-${p.id}`}>
                                <td className="px-4 py-3 text-sm">{p.employee_name}</td>
                                <td className="px-4 py-3 text-sm font-mono">₹{Number(p.base_salary).toLocaleString()}</td>
                                <td className="px-4 py-3 text-sm font-mono text-emerald-700">{p.days_present}</td>
                                <td className="px-4 py-3 text-sm font-mono text-red-600">{p.days_absent}</td>
                                <td className="px-4 py-3 text-sm font-mono">₹{Number(p.deductions).toLocaleString()}</td>
                                <td className="px-4 py-3 text-sm font-mono">₹{Number(p.statutory).toLocaleString()}</td>
                                <td className="px-4 py-3 text-sm font-mono font-semibold">₹{Number(p.net_pay).toLocaleString()}</td>
                                <td className="px-4 py-3"><span className={`status-badge ${p.status === "paid" ? "bg-emerald-100 text-emerald-800 border-emerald-200" : "bg-slate-100 text-slate-700 border-slate-200"}`}>{p.status}</span></td>
                                <td className="px-4 py-3 text-right">
                                    {p.status !== "paid" && <Button size="sm" onClick={() => markPaid(p.id)} className="bg-[#0A1C3E] h-7 text-xs" data-testid={`pay-${p.id}`}><DollarSign className="w-3 h-3 mr-1" /> Pay</Button>}
                                </td>
                            </tr>
                        ))}
                        {slips.length === 0 && <tr><td colSpan="9" className="text-center py-10 text-sm text-slate-500">No payslips generated. Click "Generate Payroll" to create for this month.</td></tr>}
                    </tbody>
                </table>
            </Card>
        </AppShell>
    );
}
